import { useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Lightbulb, Loader2 } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Mini SVG diagrams                                                  */
/* ------------------------------------------------------------------ */

function ExistingScreenDiagram() {
  return (
    <svg viewBox="0 0 200 110" className="w-full h-24" role="img" aria-label="Existing screen — measure the outside frame">
      <defs>
        <pattern id="g-mesh" width="5" height="5" patternUnits="userSpaceOnUse">
          <path d="M 0 5 L 5 0" stroke="#cbd5d0" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect x="36" y="20" width="128" height="78" rx="3" fill="#f5f7f6" stroke="var(--brand-text)" strokeWidth="2" />
      <rect x="44" y="28" width="112" height="62" fill="url(#g-mesh)" />
      {/* spring tabs */}
      <rect x="66" y="16" width="14" height="6" rx="2" fill="var(--brand-green)" opacity="0.85" />
      <rect x="120" y="16" width="14" height="6" rx="2" fill="var(--brand-green)" opacity="0.85" />
      {/* width arrow */}
      <line x1="36" y1="104" x2="164" y2="104" stroke="#9ca3af" strokeWidth="1" />
      <polygon points="36,104 41,101 41,107" fill="#9ca3af" />
      <polygon points="164,104 159,101 159,107" fill="#9ca3af" />
      <text x="100" y="103" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-text)">W (frame edge)</text>
      {/* height arrow */}
      <line x1="28" y1="20" x2="28" y2="98" stroke="#9ca3af" strokeWidth="1" />
      <polygon points="28,20 25,25 31,25" fill="#9ca3af" />
      <polygon points="28,98 25,93 31,93" fill="#9ca3af" />
      <text x="14" y="62" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-text)" transform="rotate(-90, 14, 62)">H</text>
    </svg>
  );
}

function ExistingSliderDiagram() {
  return (
    <svg viewBox="0 0 200 130" className="w-full h-32" role="img" aria-label="Existing sliding screen door — measure the outside frame">
      <defs>
        <pattern id="g-mesh-tall" width="5" height="5" patternUnits="userSpaceOnUse">
          <path d="M 0 5 L 5 0" stroke="#cbd5d0" strokeWidth="0.5" />
        </pattern>
      </defs>
      {/* tall slider door frame */}
      <rect x="78" y="10" width="44" height="110" rx="2" fill="#f5f7f6" stroke="var(--brand-text)" strokeWidth="2" />
      <rect x="84" y="16" width="32" height="98" fill="url(#g-mesh-tall)" />
      {/* top spring tabs (sliders usually have spring at top) */}
      <rect x="86" y="6" width="10" height="5" rx="1.5" fill="var(--brand-green)" opacity="0.85" />
      <rect x="104" y="6" width="10" height="5" rx="1.5" fill="var(--brand-green)" opacity="0.85" />
      {/* roller wheels at bottom */}
      <circle cx="88" cy="124" r="3" fill="#6b7280" />
      <circle cx="112" cy="124" r="3" fill="#6b7280" />
      {/* pull handle on one side */}
      <rect x="76" y="60" width="3" height="14" rx="1" fill="var(--brand-text)" />
      {/* width arrow */}
      <line x1="78" y1="124" x2="122" y2="124" stroke="#9ca3af" strokeWidth="1" />
      <polygon points="78,124 83,121 83,127" fill="#9ca3af" />
      <polygon points="122,124 117,121 117,127" fill="#9ca3af" />
      <text x="100" y="122" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-text)">W</text>
      {/* height arrow */}
      <line x1="68" y1="10" x2="68" y2="120" stroke="#9ca3af" strokeWidth="1" />
      <polygon points="68,10 65,15 71,15" fill="#9ca3af" />
      <polygon points="68,120 65,115 71,115" fill="#9ca3af" />
      <text x="54" y="65" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-text)" transform="rotate(-90, 54, 65)">H</text>
    </svg>
  );
}

function MatchingWindowDiagram() {
  return (
    <svg viewBox="0 0 200 110" className="w-full h-24" role="img" aria-label="Find a same-size window elsewhere">
      {/* wall */}
      <rect x="2" y="14" width="196" height="84" fill="#f7faf6" stroke="#e5e7eb" strokeWidth="1" />
      {/* window 1 (highlighted: this one is missing screen) */}
      <rect x="14" y="32" width="44" height="56" fill="#fff" stroke="var(--brand-text)" strokeWidth="2" />
      <line x1="14" y1="60" x2="58" y2="60" stroke="var(--brand-text)" strokeWidth="1" />
      <text x="36" y="100" textAnchor="middle" fontSize="8" fill="#6b7280">missing</text>
      {/* window 2 (matching: pull screen from this) */}
      <rect x="78" y="32" width="44" height="56" fill="#dcfce7" stroke="var(--brand-green)" strokeWidth="2" />
      <line x1="78" y1="60" x2="122" y2="60" stroke="var(--brand-green)" strokeWidth="1" />
      <text x="100" y="100" textAnchor="middle" fontSize="8" fill="var(--brand-text)" fontWeight="700">same size</text>
      {/* window 3 (different size) */}
      <rect x="142" y="44" width="44" height="32" fill="#fff" stroke="#9ca3af" strokeWidth="1.5" />
      <text x="164" y="100" textAnchor="middle" fontSize="8" fill="#9ca3af">different</text>
      {/* arrow */}
      <path d="M 100 26 Q 70 14 40 26" fill="none" stroke="var(--brand-green)" strokeWidth="1.5" strokeDasharray="3 2" />
      <polygon points="40,26 46,22 46,30" fill="var(--brand-green)" />
    </svg>
  );
}

function OpeningOnlyDiagram() {
  return (
    <svg viewBox="0 0 200 110" className="w-full h-24" role="img" aria-label="Measure the screen track">
      {/* outer wall cross-section */}
      <rect x="20" y="14" width="160" height="80" fill="#fff" stroke="var(--brand-text)" strokeWidth="2" />
      {/* screen track (highlighted) */}
      <rect x="32" y="22" width="136" height="64" fill="none" stroke="var(--brand-green)" strokeWidth="2" strokeDasharray="4 2" />
      <text x="100" y="58" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-green)">SCREEN TRACK</text>
      <text x="100" y="70" textAnchor="middle" fontSize="7" fill="#6b7280">(measure inside)</text>
      {/* width arrow */}
      <line x1="32" y1="100" x2="168" y2="100" stroke="#9ca3af" strokeWidth="1" />
      <polygon points="32,100 38,97 38,103" fill="#9ca3af" />
      <polygon points="168,100 162,97 162,103" fill="#9ca3af" />
      <text x="100" y="98" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-text)">W − 1/8&quot;</text>
    </svg>
  );
}

function SliderDoorTrackDiagram() {
  return (
    <svg viewBox="0 0 220 130" className="w-full h-32" role="img" aria-label="Slider door track — measure side jamb to center mullion, and track-to-track height">
      <defs>
        <pattern id="g-glass" width="6" height="6" patternUnits="userSpaceOnUse">
          <path d="M 0 6 L 6 0" stroke="#bfdbfe" strokeWidth="0.5" />
        </pattern>
      </defs>
      {/* outer rough opening / wall */}
      <rect x="2" y="8" width="216" height="116" fill="#f7faf6" stroke="#e5e7eb" strokeWidth="1" />
      {/* top track (darker bar) */}
      <rect x="10" y="14" width="200" height="10" fill="#e5e7eb" stroke="var(--brand-text)" strokeWidth="1.2" />
      <text x="110" y="22" textAnchor="middle" fontSize="7" fill="#6b7280">top track</text>
      {/* bottom track (where wheels roll) */}
      <rect x="10" y="106" width="200" height="10" fill="#e5e7eb" stroke="var(--brand-text)" strokeWidth="1.2" />
      <text x="110" y="113" textAnchor="middle" fontSize="7" fill="#6b7280">bottom track (wheels roll here)</text>
      {/* sliding glass pane (left half, highlighted as the moving one — where the screen door will slide) */}
      <rect x="14" y="24" width="94" height="82" fill="url(#g-glass)" stroke="#94a3b8" strokeWidth="1" />
      {/* center mullion (the vertical post the user measures TO) */}
      <rect x="108" y="24" width="4" height="82" fill="var(--brand-green)" />
      <text x="110" y="40" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="var(--brand-green)">CENTER MULLION</text>
      {/* fixed pane (right) */}
      <rect x="112" y="24" width="94" height="82" fill="url(#g-glass)" opacity="0.6" stroke="#cbd5e1" strokeWidth="1" />
      {/* side jamb (where lock latches) */}
      <rect x="10" y="24" width="4" height="82" fill="var(--brand-green)" />
      <text x="6" y="65" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="var(--brand-green)" transform="rotate(-90, 6, 65)">SIDE JAMB</text>
      {/* width arrow: side jamb (x=14, the inside of jamb) → mullion (x=108) */}
      <line x1="14" y1="65" x2="108" y2="65" stroke="var(--brand-green)" strokeWidth="2" />
      <polygon points="14,65 19,62 19,68" fill="var(--brand-green)" />
      <polygon points="108,65 103,62 103,68" fill="var(--brand-green)" />
      <rect x="44" y="58" width="34" height="14" rx="3" fill="#fff" stroke="var(--brand-green)" strokeWidth="1" />
      <text x="61" y="68" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-green)">WIDTH</text>
      {/* height arrow: top of bottom track (y=106) → top of top track (y=14) */}
      <line x1="200" y1="106" x2="200" y2="14" stroke="var(--brand-green)" strokeWidth="2" />
      <polygon points="200,106 197,101 203,101" fill="var(--brand-green)" />
      <polygon points="200,14 197,19 203,19" fill="var(--brand-green)" />
      <text x="190" y="64" textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--brand-green)" transform="rotate(-90, 190, 64)">H − 1/4&quot;</text>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Path content                                                       */
/* ------------------------------------------------------------------ */

const WINDOW_PATHS = [
  {
    id: "existing",
    label: "I have the screen",
    badge: "Best",
    badgeColor: "#16a34a",
    diagram: ExistingScreenDiagram,
    intro: "The most accurate way. Measure the screen, not the window.",
    steps: [
      "Pop the screen out of the track — squeeze the spring tabs and pull it free.",
      "Lay it flat on the floor, frame-side up.",
      "Measure the outside edge of the metal frame (not the mesh).",
      "Round to the nearest 1/16\".",
    ],
    tip: "Width is the shorter side, height is the longer side. Use a tape measure that locks.",
  },
  {
    id: "matching",
    label: "Same-size window nearby",
    badge: "Easy",
    badgeColor: "#0ea5e9",
    diagram: MatchingWindowDiagram,
    intro: "Most homes have matching windows in bedrooms or on the same wall — measure one of those.",
    steps: [
      "Find a same-size window elsewhere in your home.",
      "Pop its screen out.",
      "Measure that screen using the steps from \"I have the screen\".",
    ],
    tip: "Same window manufacturer + same glass dimensions = same screen size, every time.",
  },
  {
    id: "opening",
    label: "Just the opening",
    badge: "Last resort",
    badgeColor: "#f59e0b",
    diagram: OpeningOnlyDiagram,
    intro: "No screen and no matching window? Measure the screen track — but this is less accurate.",
    steps: [
      "Open the window all the way.",
      "Find the screen track on the outside (NOT the glass track).",
      "Measure inside-edge to inside-edge of the track for both width and height.",
      "Subtract 1/8\" from each measurement so the screen slides in cleanly.",
    ],
  },
];

const SLIDER_PATHS = [
  {
    id: "existing",
    label: "I have the slider",
    badge: "Best",
    badgeColor: "#16a34a",
    diagram: ExistingSliderDiagram,
    intro: "Measure the existing screen door, not the door frame.",
    steps: [
      "Lift the slider screen door off its track (lift up, then pull the bottom out).",
      "Lay it flat.",
      "Measure the outside edge of the metal frame.",
      "Round to the nearest 1/16\".",
    ],
    tip: "Most slider screen doors range from 78\" to 94\" tall. If yours is way outside that, double-check your measurement.",
  },
  {
    id: "track",
    label: "Just the door track",
    badge: "Last resort",
    badgeColor: "#f59e0b",
    diagram: SliderDoorTrackDiagram,
    intro: "No existing slider? Measure the door opening itself.",
    steps: [
      "For width: measure from the inside of the side jamb (the vertical track where the door's lock latches when closed) straight across to the center mullion (the vertical post that separates the fixed pane from the sliding pane).",
      "For height: measure from the top of the bottom track (the rail your door's wheels roll on) up to the top of the upper track.",
      "Subtract 1/4\" from the height so the door slides cleanly.",
      "Round both numbers to the nearest 1/16\".",
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export default function MeasurementGuide({ productType = "window", onAddProMeasurement }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const paths = productType === "window" ? WINDOW_PATHS : SLIDER_PATHS;
  const [activeId, setActiveId] = useState(paths[0].id);
  const active = paths.find((p) => p.id === activeId) || paths[0];
  const Diagram = active.diagram;
  const showProUpsell = active.id === "opening" || active.id === "track";

  const handleAddPro = async () => {
    if (!onAddProMeasurement || adding) return;
    setAdding(true);
    try { await onAddProMeasurement(); } finally { setAdding(false); }
  };

  return (
    <div
      className="rounded-2xl border-2 border-[var(--brand-border)] mb-4"
      style={{ background: "#fff" }}
      data-testid="measurement-guide"
    >
      <button
        type="button"
        data-testid="measurement-guide-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="inline-flex items-center gap-2 font-bold text-[var(--brand-text)]">
          <Lightbulb className="w-4 h-4 brand-text" />
          How do I measure?
          <span className="text-xs font-normal muted-text">— takes about 5 min</span>
        </span>
        {open ? <ChevronUp className="w-5 h-5 muted-text" /> : <ChevronDown className="w-5 h-5 muted-text" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-[var(--brand-border)]">
          <p className="text-xs muted-text mt-3 mb-2">Pick the situation that matches yours:</p>
          <div className="flex flex-wrap gap-2 mb-3" role="tablist">
            {paths.map((p) => {
              const isActive = activeId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  data-testid={`guide-tab-${p.id}`}
                  aria-selected={isActive}
                  onClick={() => setActiveId(p.id)}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs font-semibold transition-colors ${
                    isActive
                      ? "brand-bg text-white border-transparent"
                      : "border-[var(--brand-border)] text-[var(--brand-text)] hover:border-[var(--brand-green)]"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="rounded-xl p-3" style={{ background: "#f7faf6" }}>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                style={{ background: active.badgeColor }}
              >
                {active.badge}
              </span>
              <span className="font-semibold text-[var(--brand-text)] text-sm">{active.label}</span>
            </div>
            <p className="text-xs muted-text mb-3">{active.intro}</p>

            <Diagram />

            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[var(--brand-text)] mt-3">
              {active.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>

            {active.tip && (
              <p className="text-xs mt-3 px-2 py-2 rounded-lg flex items-start gap-1.5"
                 style={{ background: "#dcfce7", color: "#166534" }}>
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{active.tip}</span>
              </p>
            )}

            {/* Pro Measurement upsell on the risky tabs — clickable, hits /api/orders/{id}/upsells */}
            {showProUpsell && onAddProMeasurement && (
              <div
                className="mt-3 px-3 py-3 rounded-lg border-2"
                style={{ background: "#fef3c7", borderColor: "#f59e0b" }}
                data-testid="guide-pro-upsell"
              >
                <p className="text-xs flex items-start gap-1.5 font-semibold" style={{ color: "#92400e" }}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Not confident? Skip this and have us measure for you.</span>
                </p>
                <p className="text-[11px] mt-1 mb-2" style={{ color: "#92400e" }}>
                  We&apos;ll send a tech, take precise measurements, and start production from there. No need to enter measurements below.
                </p>
                <button
                  type="button"
                  data-testid="guide-add-pro-btn"
                  disabled={adding}
                  onClick={handleAddPro}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full font-bold text-sm bg-white border-2 border-[#92400e] text-[#92400e] hover:bg-[#fde68a] transition-colors disabled:opacity-60"
                >
                  {adding ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Adding…
                    </>
                  ) : (
                    <>Add Pro Measurement <span className="brand-text">+ $39</span></>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
